import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import enquiryService from "../api/enquiryService";
import brandService from "../api/brandService"; 
import budgetService from "../api/budgetService";
import {
  Table, TableBody, TableCell, TableHeader, TableRow,
} from "../components/ui/table";
import { 
  ArrowLeft, Calendar, User, Building2, Mail, Phone, Lock, Unlock, 
  Save, Plus, MessageSquare, Clock, ChevronDown, ChevronUp, Pencil, Trash2,
  Briefcase, GraduationCap, Wallet, Globe, Tag, X, Activity, Landmark, Award, AlertCircle, Check
} from "lucide-react";
import Button from "../components/ui/button/Button";

export default function ClientDetail({ isDark }) {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const isNewClient = clientId === 'new';
  
  const [isEditing, setIsEditing] = useState(isNewClient);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConvForm, setShowConvForm] = useState(false);
  
  const [brands, setBrands] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [editingConvId, setEditingConvId] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const [newConv, setNewConv] = useState({ c_title: "", interaction_date: today, c_desc: "" });

  const [profile, setProfile] = useState({
    enquirer_name: "", enq_email: "", enq_number: "", enquirer_company: "",
    college_name: "", course_taken: "", passing_grade: "", job_title: "",
    job_company_location: "", job_field: "", brand_id: "", service_id: "",
    enquiry_status: "just connected", budget_range_id: "",  next_follow_up: ""
  });

  // User Preference: Slate 300 (Dark) / 600 (Light)
  const secondaryText = isDark ? 'text-slate-300' : 'text-slate-600';

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        const [brandsRes, budgetRes, clientData] = await Promise.all([
          brandService.getAllBrands(),
          budgetService.getActiveBudgets(),
          !isNewClient ? enquiryService.getEnquiryDetail(clientId) : Promise.resolve(null)
        ]);

        const budgetsData = budgetRes?.data || budgetRes || [];
        setBudgets(budgetsData);

        const brandsData = brandsRes?.data || brandsRes || [];
        setBrands(brandsData.filter(b => b.status === 'active' || b.is_active === true || b.is_active === 1));

        if (clientData) {
          const profileData = clientData.profile || clientData;
          
          // Clean date for the HTML input
          if (profileData.next_follow_up) {
            profileData.next_follow_up = profileData.next_follow_up.split(' ')[0];
          }

          setProfile(profileData);
          setConversations(clientData.conversations || []);

          if (profileData.brand_id) {
            const brandDetails = await brandService.getBrandDetail(profileData.brand_id);
            setFilteredServices(brandDetails.services || []);
          }
        }
      } catch (error) {
        console.error("Initialization failed", error);
        toast.error("Failed to load dossier");
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [clientId, isNewClient]);

  const handleBrandChange = async (brandId) => {
    setProfile({ ...profile, brand_id: brandId, service_id: "" });
    if (brandId) {
      try {
        const details = await brandService.getBrandDetail(brandId);
        setFilteredServices(details.services || []);
      } catch (e) {
        console.error("Service fetch failed", e);
      }
    } else {
      setFilteredServices([]);
    }
  };

  const handleDeleteClient = async () => {
    // Reusing the specific toast style from your brand detail example
    const deleteToastStyle = {
      style: {
        borderRadius: '12px',
        background: isDark ? '#020617' : '#ffffff', 
        color: isDark ? '#cbd5e1' : '#475569',      
        border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: '800',
        padding: '12px 20px',
      },
    };

    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <p className="font-bold text-[10px] tracking-widest text-orange-600 uppercase">
            Confirm Client Purge
          </p>
          <p className="text-[11px] text-slate-500 leading-normal normal-case ">
            Warning: This will permanently remove this client and all linked conversation logs.
          </p>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              const loadingToast = toast.loading("PURGING DOSSIER...", deleteToastStyle);
              try {
                await enquiryService.deleteEnquiry(clientId);
                toast.success(`DOSSIER PURGED SUCCESSFULLY`, { ...deleteToastStyle, id: loadingToast });
                
                setTimeout(() => {
                  navigate('/enquiry');
                }, 1200);
              } catch (err) { 
                toast.error("PURGE FAILED", { ...deleteToastStyle, id: loadingToast }); 
              }
            }} 
            className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg active:scale-95"
          >
            Confirm Wipeout
          </button>
          <button 
            onClick={() => toast.dismiss(t.id)} 
            className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase opacity-70 hover:opacity-100"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { ...deleteToastStyle, duration: 4000, icon: <Trash2 size={20} className="text-orange-600 shrink-0 normal-case" /> });
  };

  const handleSaveProfile = async () => {
    if (!profile.enquirer_name?.trim() || !profile.enq_email?.trim() || !profile.enq_number?.trim()) {
        toast.error("Required fields missing", { icon: '⚠️' });
        return;
    }

    const loadingToast = toast.loading('Synchronizing Dossier...');
    
    try {
      // STRIP NESTED ARRAYS: This prevents the backend from hanging
      const { 
        conversations: _c, 
        brand_name: _bn, 
        service_name: _sn, 
        range_label: _rl, 
        ...payload 
      } = profile;

      if (isNewClient) {
        await enquiryService.createEnquiry(payload);
        toast.success('Dossier Created!', { id: loadingToast });
        setTimeout(() => navigate('/enquiry'), 1000);
      } else {
        await enquiryService.updateEnquiry(clientId, payload);
        toast.success('Dossier Updated!', { id: loadingToast });
        setIsEditing(false);
        
        // Refresh local data
        const updated = await enquiryService.getEnquiryDetail(clientId);
        const refreshedProfile = updated.profile || updated;
        if (refreshedProfile.next_follow_up) {
            refreshedProfile.next_follow_up = refreshedProfile.next_follow_up.split(' ')[0];
        }
        setProfile(refreshedProfile);
      }
    } catch (error) { 
        console.error("Save Error:", error);
        toast.error("Server Error: Check your database connection", { id: loadingToast }); 
    }
  };

  const handleSaveConversation = async (convData = newConv, id = null) => {
    if(!convData.c_title || !convData.interaction_date) {
        toast.error("Title and Date are required");
        return;
    }

    const loadId = toast.loading("Saving interaction...");
    try {
      if (id) {
        await enquiryService.updateConversation(clientId, id, convData);
        toast.success("Record Updated", { id: loadId });
      } else {
        await enquiryService.addConversation({ ...convData, enquiry_id: clientId, logged_by: 'Admin' });
        toast.success("Interaction Logged", { id: loadId });
      }
      setShowConvForm(false);
      setEditingConvId(null);
      setNewConv({ c_title: "", interaction_date: today, c_desc: "" });
      const updatedData = await enquiryService.getEnquiryDetail(clientId);
      setConversations(updatedData.conversations || []);
    } catch (e) { 
      toast.error("Failed to save entry", { id: loadId }); 
    }
  };

  const handleDeleteConversation = (id) => {
    toast((t) => (
      <div className="flex flex-col gap-3 py-1">
        <div className="flex items-center gap-2">
          <AlertCircle size={18} className="text-red-500" />
          <span className="font-brand-heading text-[10px] tracking-widest">
            Confirm Deletion?
          </span>
        </div>
        <p className={`text-[11px] normal-case font-brand-body leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          This will permanently purge this interaction record.
        </p>
        <div className="flex gap-2 justify-end mt-2">
          <button
            onClick={() => toast.dismiss(t.id)}
            className={`px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest border ${
              isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              executeDelete(id);
            }}
            className="px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    ), {
      duration: 5000,
      position: 'top-center',
      style: {
        minWidth: '300px',
        border: isDark ? '1px solid #ef444455' : '1px solid #fee2e2',
      }
    });
  };

  const executeDelete = async (id) => {
    const loadId = toast.loading("Purging interaction record...");
    try {
      await enquiryService.deleteConversation(id);
      toast.success("Record Deleted", { id: loadId });
      setConversations(conversations.filter(c => c.c_id !== id));
      setEditingConvId(null);
    } catch (e) { 
      toast.error("Delete failed", { id: loadId }); 
    }
  };

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
        
        <div className="animate-pulse text-[#2D4D94] font-brand-heading uppercase tracking-[0.3em] text-[10px]">
          Initialising Client Dossier...
        </div>
      </div>
    );
  }

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>


      {/* Hero Header */}
      {/* Hero Header */}
{/* Hero Header */}
<div className="max-w-7xl mx-auto w-full mb-10">
  <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4 md:gap-0">
    
    {/* 1. Back Button - Always First */}
    <div className="flex justify-start">
      <Button 
        variant="action" 
        isDark={isDark} 
        onClick={() => navigate('/enquiry')} 
        className="relative"
      >
        <ArrowLeft size={24} strokeWidth={3} />
      </Button>
    </div>

    {/* 2. Heading - Middle on Laptop, Second on Mobile */}
    <div className="text-center space-y-1">
      <h2 className={`text-2xl md:text-2xl font-bold font-brand-heading tracking-normal ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
        {profile.enquirer_name || "New Lead"}
      </h2>
      <div className={`flex items-center justify-center gap-2 text-[13px] font-brand-body-bold uppercase tracking-widest ${isDark ? 'text-indigo-400' : 'text-[#2D4D94]'}`}>
        <Building2 size={14} /> {profile.enquirer_company || "Company Unassigned"}
      </div>
    </div>

    {/* 3. Actions - Right on Laptop, Third on Mobile */}
    <div className="flex justify-center md:justify-end gap-3 mt-2 md:mt-0 items-center">
      {!isNewClient && (
        <Button 
          variant={isEditing ? "primary" : "action"} 
          isDark={isDark} 
          onClick={() => setIsEditing(!isEditing)} 
          className="transition-all duration-300"
        >
          {isEditing ? (
            <>
              <Unlock size={18} className="mr-2" /> 
              <span className="font-brand-heading text-[10px] tracking-widest uppercase">Editing</span>
            </>
          ) : (
            <Lock size={18} />
          )}
        </Button>
      )}

      {isEditing && (
        <div className="flex items-center gap-2 min-w-[200px]">
          {/* Commit Button - 80% */}
          <Button 
            variant="primary" 
            isDark={isDark} 
            onClick={handleSaveProfile} 
            className="shadow-lg px-8 font-brand-heading flex-grow"
          >
            <Save size={16} className="mr-2" /> Commit
          </Button>

          {/* Delete Button - 20% (Visible only when editing an existing client) */}
          {!isNewClient && (
            <button 
              onClick={handleDeleteClient}
              title="Purge Dossier"
              className={`group h-[48px] px-4 flex items-center justify-center rounded-xl transition-all border shadow-sm active:scale-95
                ${isDark 
                  ? 'bg-slate-900/50 border-slate-700 hover:bg-rose-950/20 text-rose-400' 
                  : 'bg-white border-slate-200 hover:bg-rose-50 text-rose-600'}`}
            >
              <Trash2 size={18} className="text-orange-600 transition-transform group-hover:scale-110 group-hover:text-rose-500" />
            </button>
          )}
        </div>
      )}
    </div>

  </div>
</div>

      {/* Profile Card */}
      <div className={`max-w-7xl mx-auto rounded-2xl border transition-all duration-500 shadow-sm ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
        <div className="p-10">
          {isEditing ? (
            <div className="space-y-10 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <DetailInput label="Full Name" required icon={User} isDark={isDark} value={profile.enquirer_name} onChange={(v) => setProfile({...profile, enquirer_name: v})} />
                <DetailInput label="Email" required icon={Mail} isDark={isDark} value={profile.enq_email} onChange={(v) => setProfile({...profile, enq_email: v})} />
                <DetailInput label="Phone" required icon={Phone} isDark={isDark} value={profile.enq_number} onChange={(v) => setProfile({...profile, enq_number: v})} />
                <DetailInput label="Company" icon={Building2} isDark={isDark} value={profile.enquirer_company} onChange={(v) => setProfile({...profile, enquirer_company: v})} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-slate-100 dark:border-slate-800">
                <DetailInput label="College" icon={GraduationCap} isDark={isDark} value={profile.college_name} onChange={(v) => setProfile({...profile, college_name: v})} />
                <DetailInput label="Course" icon={Tag} isDark={isDark} value={profile.course_taken} onChange={(v) => setProfile({...profile, course_taken: v})} />
                <DetailInput label="Passing Grade" icon={Award} isDark={isDark} value={profile.passing_grade} onChange={(v) => setProfile({...profile, passing_grade: v})} />
                <DetailInput label="Job Title" icon={Briefcase} isDark={isDark} value={profile.job_title} onChange={(v) => setProfile({...profile, job_title: v})} />
                <DetailInput label="Job Field" icon={Activity} isDark={isDark} value={profile.job_field} onChange={(v) => setProfile({...profile, job_field: v})} />
                <DetailInput label="Job Location" icon={Globe} isDark={isDark} value={profile.job_company_location} onChange={(v) => setProfile({...profile, job_company_location: v})} />
                <div className="space-y-1.5 text-left">
                  <label className={`text-xs font-brand-heading uppercase tracking-widest ml-1 ${secondaryText}`}>Budget Range</label>
                  <div className="relative">
                    <Wallet size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2D4D94]/60" />
                    <select 
                      value={profile.budget_range_id || ""} 
                      onChange={(e) => setProfile({...profile, budget_range_id: e.target.value})} 
                      className={`w-full rounded-2xl pl-11 pr-4 py-3.5 text-[12px] font-brand-body-bold outline-none border transition-all ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <option value="">Select Budget</option>
                      {budgets.map(b => <option key={b.id} value={b.id}>{b.range_label}</option>)}
                    </select>
                  </div>
                </div>                
                <DetailInput label="Follow Up" type="date" icon={Calendar} isDark={isDark} value={profile.next_follow_up || ""} onChange={(v) => setProfile({...profile, next_follow_up: v})} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-8 border-t border-slate-100 dark:border-slate-800">
                  <div className="space-y-1.5 text-left">
                    <label className={`text-xs font-brand-heading uppercase tracking-widest ml-1 ${secondaryText}`}>Enquiry Status <span className="text-red-500">*</span></label>
                    <select value={profile.enquiry_status} onChange={(e) => setProfile({...profile, enquiry_status: e.target.value})} className={`w-full rounded-2xl px-4 py-3 text-[12px] font-brand-body-bold outline-none border transition-all ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'}`}>
                        <option value="just connected">Just Connected</option>
                        <option value="started with adv. payment">Started with Adv. Payment</option>
                        <option value="in progress">In Progress</option>
                        <option value="closed">Closed</option>
                        <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div className="space-y-1.5 text-left">
                    <label className={`text-xs font-brand-heading uppercase tracking-widest ml-1 ${secondaryText}`}>Brand Association</label>
                    <select value={profile.brand_id || ""} onChange={(e) => handleBrandChange(e.target.value)} className={`w-full rounded-2xl px-4 py-3 text-[12px] font-brand-body-bold outline-none border transition-all ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'}`}>
                      <option value="">Select Brand</option>
                      {brands.map(b => <option key={b.id} value={b.id}>{b.brand_name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5 text-left">
                    <label className={`text-xs font-brand-heading uppercase tracking-widest ml-1 ${secondaryText}`}>Target Service</label>
                    <select value={profile.service_id || ""} disabled={!profile.brand_id} onChange={(e) => setProfile({...profile, service_id: e.target.value})} className={`w-full rounded-2xl px-4 py-3 text-[12px] font-brand-body-bold outline-none border disabled:opacity-30 transition-all ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'}`}>
                      <option value="">Select Service</option>
                      {filteredServices.map(s => <option key={s.id} value={s.id}>{s.service_name}</option>)}
                    </select>
                  </div>
              </div>
            </div>
          ) : (
            <div className="space-y-8 animate-in fade-in duration-500">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-y-10 gap-x-12 text-left">
                <InfoItem label="Email Address" value={profile.enq_email} icon={Mail} secondaryText={secondaryText} isDark={isDark} />
                <InfoItem label="Phone Number" value={profile.enq_number} icon={Phone} secondaryText={secondaryText} isDark={isDark} />
                <InfoItem label="Status" value={profile.enquiry_status} icon={Activity} secondaryText={secondaryText} isDark={isDark} isStatus />
              </div>

              {isExpanded && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-y-10 gap-x-12 pt-10 border-t border-slate-100 dark:border-slate-800 animate-in slide-in-from-top-4 text-left">
                  <InfoItem label="College" value={profile.college_name} icon={GraduationCap} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Course" value={profile.course_taken} icon={Tag} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Passing Grade" value={profile.passing_grade} icon={Award} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Job Title" value={profile.job_title} icon={Briefcase} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Job Field" value={profile.job_field} icon={Activity} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Location" value={profile.job_company_location} icon={Globe} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Budget" value={profile.range_label} icon={Wallet} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Follow Up" value={profile.next_follow_up} icon={Calendar} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Assigned Brand" value={brands.find(b => b.id == profile.brand_id)?.brand_name} icon={Landmark} secondaryText={secondaryText} isDark={isDark} />
                  <InfoItem label="Target Service" value={filteredServices.find(s => s.id == profile.service_id)?.service_name} icon={Briefcase} secondaryText={secondaryText} isDark={isDark} />
                </div>
              )}

              <div className="flex justify-center pt-4">
                <button onClick={() => setIsExpanded(!isExpanded)} className={`flex items-center gap-2 text-[10px] font-brand-heading uppercase tracking-[0.25em] ${secondaryText} hover:text-[#2D4D94] transition-all`}>
                  {isExpanded ? <><ChevronUp size={16}/> Condense Dossier</> : <><ChevronDown size={16}/> Expand Full Profile</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Interactions Timeline */}
      <div className="max-w-7xl mx-auto space-y-4 pb-12">
        <div className="flex items-center justify-between px-4">
            <h3 className={`font-brand-heading uppercase text-xs tracking-[0.2em] ${secondaryText}`}>Interaction Timeline</h3>
            {!showConvForm && (
                <Button 
                  variant="primary"
                  isDark={isDark}
                  onClick={() => setShowConvForm(true)} 
                  className="shadow-md font-brand-heading !rounded-xl"
                >
                  <Plus size={16} /> Log Interaction
                </Button>
            )}
        </div>

        {showConvForm && (
          <div className={`p-8 rounded-2xl border shadow-2xl animate-in zoom-in-95 duration-200 ${isDark ? 'bg-slate-900 border-indigo-500/30' : 'bg-white border-indigo-100'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DetailInput isDark={isDark} label="Subject" icon={MessageSquare} value={newConv.c_title} onChange={(v) => setNewConv({...newConv, c_title: v})} />
              <DetailInput isDark={isDark} label="Interaction Date" icon={Calendar} type="date" value={newConv.interaction_date} onChange={(v) => setNewConv({...newConv, interaction_date: v})} />
              <div className="md:col-span-2">
                 <textarea value={newConv.c_desc} onChange={(e) => setNewConv({...newConv, c_desc: e.target.value})} placeholder="Detailed summary of the conversation..." className={`w-full rounded-2xl p-4 text-[13px] border outline-none min-h-[140px] font-brand-body transition-all focus:border-indigo-500 ${isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-slate-50 border-slate-200'}`} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3">
                    <Button variant="ghost" isDark={isDark} className="font-brand-heading" onClick={() => setShowConvForm(false)}>Cancel</Button>
                    <Button 
                      variant="primary" 
                      isDark={isDark} 
                      onClick={() => handleSaveConversation()} 
                      className="font-brand-heading px-8"
                    >
                      Save Record
                    </Button>
              </div>
            </div>
          </div>
        )}

        <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'}`}>
          <Table className="table-fixed w-full"> 
            <TableHeader className={isDark ? 'bg-slate-800/50' : 'bg-slate-50'}>
              <TableRow className="border-none">
                <TableCell isHeader className="px-10 py-5 text-xs font-brand-heading uppercase tracking-widest text-[#2D4D94] w-[25%] border-none">Context</TableCell>
                <TableCell isHeader className="px-10 py-5 text-xs font-brand-heading uppercase tracking-widest text-[#2D4D94] w-[60%] border-none">Summary</TableCell>
                <TableCell isHeader className="px-10 py-5 text-right text-xs font-brand-heading uppercase tracking-widest text-[#2D4D94] w-[15%] border-none">Action</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conversations.length > 0 ? conversations.map((conv) => {
                const isThisEditing = editingConvId === conv.c_id;
                return (
                    <TableRow key={conv.c_id} className={`group border-none transition-colors ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50'} ${isThisEditing ? (isDark ? 'bg-slate-800/40' : 'bg-indigo-50/30') : ''}`}>
                    <TableCell className="px-10 py-6 align-top border-none text-left">
                        {isThisEditing ? (
                            <div className="space-y-4">
                                <input type="date" value={conv.interaction_date} onChange={(e) => {
                                    const updated = conversations.map(c => c.c_id === conv.c_id ? {...c, interaction_date: e.target.value} : c);
                                    setConversations(updated);
                                }} className={`w-full bg-transparent border-b border-indigo-500 text-[12px] p-1 outline-none ${isDark ? 'text-white' : 'text-slate-900'}`} />
                                <input value={conv.c_title} onChange={(e) => {
                                    const updated = conversations.map(c => c.c_id === conv.c_id ? {...c, c_title: e.target.value} : c);
                                    setConversations(updated);
                                }} className={`w-full bg-transparent border-b border-indigo-500 text-[13px] font-bold p-1 outline-none uppercase ${isDark ? 'text-white' : 'text-slate-900'}`} />
                            </div>
                        ) : (
                            <>
                                <div className="text-[10px] font-bold text-[#2D4D94] uppercase tracking-wider mb-1">{conv.interaction_date}</div>
                                <div className={`font-brand-body-bold text-[13px] uppercase leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    {conv.c_title}
                                </div>
                            </>
                        )}
                    </TableCell>
                    <TableCell className="px-10 py-6 align-top border-none text-left">
                        {isThisEditing ? (
                            <textarea value={conv.c_desc} onChange={(e) => {
                                const updated = conversations.map(c => c.c_id === conv.c_id ? {...c, c_desc: e.target.value} : c);
                                setConversations(updated);
                            }} className={`w-full bg-transparent border border-indigo-500/30 rounded-lg p-3 text-[12px] min-h-[80px] outline-none ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
                        ) : (
                            <div className={`text-[12px] leading-relaxed whitespace-pre-wrap break-words ${secondaryText}`}>
                                {conv.c_desc}
                            </div>
                        )}
                    </TableCell>
                    <TableCell className="px-10 py-6 text-right align-top border-none">
                        <div className="flex justify-end gap-2">
                            {isThisEditing ? (
                                <>
                                    <button onClick={() => handleSaveConversation(conv, conv.c_id)} className="p-2 bg-green-500/10 hover:bg-green-500/20 rounded-xl text-green-500 transition-all">
                                        <Check size={18} />
                                    </button>
                                    <button onClick={() => handleDeleteConversation(conv.c_id)} className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-xl text-red-500 transition-all">
                                        <Trash2 size={18} />
                                    </button>
                                    <button onClick={() => setEditingConvId(null)} className="p-2 hover:bg-slate-500/10 rounded-xl text-slate-400 transition-all">
                                        <X size={18} />
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => setEditingConvId(conv.c_id)} className="p-2.5 hover:bg-indigo-500/10 rounded-xl text-[#2D4D94] transition-all">
                                    <Pencil size={18} />
                                </button>
                            )}
                        </div>
                    </TableCell>
                    </TableRow>
                )
              }) : (
                <TableRow><TableCell colSpan={3} className="py-24 text-center opacity-30 text-[10px] uppercase tracking-[0.3em] font-brand-heading border-none">Zero Interactions Logged</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value, icon: Icon, secondaryText, isDark, isStatus }) {
  return (
    <div className="flex gap-4 group">
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-all ${isDark ? 'bg-slate-800 text-indigo-400 group-hover:bg-indigo-500/10' : 'bg-indigo-50 text-[#2D4D94]'}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className={`text-xs font-brand-heading uppercase tracking-widest opacity-60 mb-1 ${secondaryText}`}>{label}</div>
        <div className={`text-[14px] font-brand-body-bold ${isDark ? 'text-slate-100' : 'text-slate-900'} ${isStatus ? 'text-[#2D4D94] capitalize' : ''}`}>
          {value || <span className="opacity-20 italic">---</span>}
        </div>
      </div>
    </div>
  );
}

function DetailInput({ label, icon: Icon, isDark, onChange, required, ...props }) {
  const secondaryText = isDark ? 'text-slate-300' : 'text-slate-600';
  return (
    <div className="text-left w-full space-y-1.5">
      <label className={`text-xs font-brand-heading uppercase block ml-1 tracking-widest ${secondaryText}`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <Icon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2D4D94]/60" />
        <input 
          {...props} 
          onChange={(e) => onChange?.(e.target.value)} 
          className={`w-full rounded-2xl pl-11 pr-4 py-3.5 text-[13px] font-brand-body-bold outline-none border transition-all ${isDark ? 'bg-slate-950 border-slate-800 text-slate-100 focus:border-indigo-500' : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-indigo-500'}`} 
        />
      </div>
    </div>
  );
}